import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { createTeamMember, deleteTeamMember, getTeamMember, getTeamMembers, updateTeamMember } from './api';
import { useNavigate, useParams } from 'react-router-dom';
import { queryClient } from 'providers/AllProviders';
import { TeamMember, TeamMemberRequest } from './types';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';

export function useGetTeamMembers(
  params: any,
  options?: UseQueryOptions<{ data: TeamMember[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: TeamMember[]; meta: MetaApiResponse }>({
    queryKey: ['teamMembers', params],
    queryFn: () => getTeamMembers(params),
    ...options
  });
}

export function useGetTeamMember(params?: Record<string, any>, options?: UseQueryOptions<TeamMember, Error>) {
  const { teamId } = useParams<{ teamId: string }>();
  return useQuery<TeamMember>({
    queryKey: ['teamMember', teamId, params],
    queryFn: () => getTeamMember(teamId!, params),
    enabled: Boolean(teamId),
    ...options
  });
}

export function useCreateOrUpdateTeamMember() {
  const navigate = useNavigate();
  const { teamId } = useParams<{ teamId: string }>();

  return useMutation({
    mutationFn: (payload: TeamMemberRequest) =>
      teamId ? updateTeamMember(teamId!, payload) : createTeamMember(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['teamMembers'],
        refetchType: 'active'
      });
      if (teamId) {
        queryClient.invalidateQueries({
          queryKey: ['teamMember', teamId],
          refetchType: 'active'
        });
      }
      navigate(-1);
    }
  });
}

export function useDeleteTeamMember() {
  return useMutation({
    mutationFn: (id: string) => deleteTeamMember(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['teamMembers'],
        refetchType: 'active'
      });
    }
  });
}
